/**
 * useSocialLinks Hook
 * Centralized React hook to fetch and manage social links
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatSocialLinks } from '../utils/socialIcons';
import { API_URL } from '../utils/apiUrl';

/**
 * Custom hook to fetch global social links
 * @param {string} profileKey - Profile key (default: "default")
 * @returns {Object} { socialLinks, loading, error, refetch }
 */
export const useSocialLinks = (profileKey = 'default') => {
  const [socialLinks, setSocialLinks] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSocialLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_URL}/api/social-links/`,
        { params: { profile_key: profileKey } }
      );
      
      setRawData(response.data);
      const formatted = formatSocialLinks(response.data);
      setSocialLinks(formatted);
    } catch (err) {
      console.error('Error fetching social links:', err);
      setError(err.message);
      setSocialLinks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocialLinks();
  }, [profileKey]);

  return {
    socialLinks,      // Formatted array of {platform, url, icon, name, color}
    rawData,          // Raw data from API
    loading,          // Loading state
    error,            // Error message if any
    refetch: fetchSocialLinks  // Function to refetch
  };
};

/**
 * Hook to get only active social links (from /active endpoint)
 * More efficient if you only need non-null links
 */
export const useActiveSocialLinks = (profileKey = 'default') => {
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActiveSocialLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_URL}/api/social-links/active`,
        { params: { profile_key: profileKey } }
      );
      
      // Response already filtered on backend
      const links = response.data.links || [];
      setSocialLinks(links);
    } catch (err) {
      console.error('Error fetching active social links:', err);
      setError(err.message);
      setSocialLinks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveSocialLinks();
  }, [profileKey]);

  return {
    socialLinks,
    loading,
    error,
    refetch: fetchActiveSocialLinks
  };
};

export default useSocialLinks;
